import { Outlet } from 'react-router-dom'
import { useParams } from 'react-router-dom'

import { FlowPageDocument } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'

import useFetchLegalEntityContractData from 'modules/contracts/hooks/useFetchLegalEntityContractData'
import { ContractFlowProvider } from 'modules/contracts/lib/contractFlowContext'
import contractVersionsPayrollPolicies, {
  ContractVersionsPayrollPoliciesProvider,
} from 'modules/contracts/store/collections/contractVersionsPayrollPolicies'
import jobSalaryRangesCollection from 'modules/job_catalog/store/collections/salaryRanges'
import Loading from 'modules/layout/components/Page/Loading'
import policies, { PoliciesProvider } from 'modules/payroll_bundle/store/collections/policies'

export type ContractFlowType = 'fix' | 'promote' | 'update' | 'request_promote' | 'request_update'

const useFetchData = createAsyncData((contractVersionId: number) => {
  const data = contractVersionId
    ? {
        contract_version_ids: [contractVersionId],
      }
    : {}
  return Promise.all([
    contractVersionsPayrollPolicies.fetch({
      data: data,
    }),
    jobSalaryRangesCollection.fetch(),
  ])
})

const FlowPage = () => {
  const params = useParams()
  const { data, loading: loadingFlowPage } = useQuery(FlowPageDocument, {
    variables: {
      employeeId: Number(params.id),
    },
  })
  const employee = data?.employees?.employeesConnection.nodes[0]
  const contractVersionId = params.contractVersionId
  const contractVersions = data?.contracts?.contractVersionsConnection.nodes ?? []
  const loadingContractData = useFetchData(contractVersionId)
  const legalEntityContractData = useFetchLegalEntityContractData(
    employee?.legalEntity?.id ?? 0,
    employee?.legalEntity?.country ?? '',
    true
  )
  const isLoading = loadingContractData || legalEntityContractData.loading || loadingFlowPage

  if (isLoading) return <Loading />
  const targetVersion = contractVersions.find((version) =>
    contractVersionId ? version.id === Number(contractVersionId) : version.isReference
  )

  const contractVersionPayrollPolicy = contractVersionsPayrollPolicies.isEmpty
    ? null
    : contractVersionsPayrollPolicies.models.find(
        (policy) => policy.get('contract_version_id') === targetVersion?.id
      )

  return (
    <ContractFlowProvider
      salaryRanges={jobSalaryRangesCollection.models}
      contractVersionPayrollPolicy={contractVersionPayrollPolicy ?? null}
      legalEntityContractData={legalEntityContractData}
    >
      <ContractVersionsPayrollPoliciesProvider collection={contractVersionsPayrollPolicies}>
        <PoliciesProvider collection={policies}>
          <Outlet context={{ contractVersions }} />
        </PoliciesProvider>
      </ContractVersionsPayrollPoliciesProvider>
    </ContractFlowProvider>
  )
}

export default observer(FlowPage)
